import { Link } from 'react-router-dom';
import { ArrowRight, Check, BookOpen, DollarSign, Clock, Shield, Wrench, AlertTriangle, TrendingDown, Calculator, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import SEOHead from '../../components/SEOHead';
import { getClusterLinks } from '../../data/internalLinks';
import { PORTAL_LINKS } from '../../constants/site';

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Resources', path: '/blog' },
  { name: 'Equipment Finance Guide', path: '/equipment-finance-guide-australia' },
];

const FAQS = [
  { q: 'What is equipment finance?', a: 'Equipment finance is a form of commercial lending that allows businesses to purchase equipment, machinery, vehicles, and other business assets by spreading the cost over regular payments. The equipment itself typically serves as security for the loan, meaning you do not need to provide property or other collateral.' },
  { q: 'What types of equipment can I finance?', a: 'Virtually any business equipment can be financed: trucks, excavators, manufacturing machinery, medical devices, IT infrastructure, commercial kitchen equipment, agricultural equipment, and more. If it has a business purpose and holds value, it can be financed.' },
  { q: 'Which finance structure is best for my business?', a: 'It depends on your priorities. A chattel mortgage is best if you want immediate ownership and GST benefits. A finance lease is ideal for lower monthly payments. Hire purchase suits businesses wanting simplicity. An operating lease works for equipment you plan to upgrade regularly. Flash Funding can help you compare.' },
  { q: 'How much deposit do I need?', a: 'Many lenders offer zero-deposit equipment finance for established businesses. A deposit of 10-20% can secure better rates. Startups and credit-impaired businesses may need 20-30%. Use our equipment finance calculator to see how deposit affects your repayments.' },
  { q: 'Is equipment finance tax deductible?', a: 'Yes. Depending on the structure, you can claim interest deductions, depreciation, and GST credits. Chattel mortgages allow upfront GST claiming. Finance lease payments are generally deductible to the extent the asset is used for business. Consult your accountant for advice specific to your situation.' },
  { q: 'How fast can I get approved?', a: 'Pre-approvals can be issued within hours. Full approval typically takes 1-2 business days for straightforward applications. Low-doc applications are often approved within 24-48 hours. Timing depends on the applicant, the lender’s credit criteria and the type of loan. Flash Funding prioritises speed through its network of fast-moving lenders.' },
  { q: 'Can I finance used equipment?', a: 'Yes. Most lenders finance used equipment based on its current market value and remaining useful life. Age limits vary by lender and equipment type, but equipment up to 15-20 years old can often be financed.' },
  { q: 'What if I have bad credit?', a: 'Specialist lenders work with businesses that have impaired credit. Rates will be higher, but approval is often possible. Strong bank statement activity, a deposit, and a quality asset all help. Flash Funding knows which lenders specialise in different credit profiles.' },
  { q: 'When should I refinance my equipment?', a: 'Consider refinancing when rates have dropped since your original loan, your credit profile has improved, you want to lower monthly payments, or you need to release equity. Even a 1-2% rate reduction can save thousands over the remaining term.' },
  { q: 'How does Flash Funding compare to going to a bank?', a: 'A bank can only offer its own products. As a broker, Flash Funding can compare options from a panel of banks and non-bank specialist lenders, match your scenario to lenders whose policy suits it, and manage the application through to settlement.' },
];

export default function EquipmentFinanceGuide() {
  const clusters = getClusterLinks('equipment-finance-guide-australia', 'asset');

  return (
    <>
      <SEOHead
        title="The Complete Guide to Equipment Finance in Australia"
        description="Everything you need to know about equipment finance in Australia. Structures, rates, tax benefits, approval process, deposit requirements, and expert tips. Updated 2026."
        canonical="/equipment-finance-guide-australia"
        keywords="equipment finance guide australia, how equipment finance works, equipment finance explained, business equipment loan guide, commercial finance guide australia"
        breadcrumbs={BREADCRUMBS}
        faqs={FAQS}
        schema={[{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "The Complete Guide to Equipment Finance in Australia",
          "description": "Everything you need to know about equipment finance in Australia.",
          "datePublished": "2026-02-20",
          "dateModified": "2026-02-20",
          "author": { "@type": "Organization", "name": "Flash Funding" },
          "publisher": { "@type": "Organization", "name": "Flash Funding", "url": "https://www.flashfunding.loan" }
        }]}
      />

      {/* Hero */}
      <section className="relative bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-navy-900" />
        <div className="relative container mx-auto px-4 py-20 lg:py-28">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8" data-testid="breadcrumbs">
            {BREADCRUMBS.map((bc, i) => (
              <span key={bc.path} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {i < BREADCRUMBS.length - 1 ? (
                  <Link to={bc.path} className="hover:text-purple-400">{bc.name}</Link>
                ) : (
                  <span className="text-purple-400">{bc.name}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600/10 border border-purple-500/30 rounded-lg mb-6">
              <BookOpen size={16} className="text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">Ultimate Guide</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white uppercase leading-tight mb-6">
              The Complete Guide to<br /><span className="text-purple-400">Equipment Finance</span><br />in Australia
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
              Everything you need to know about financing business equipment in Australia — from choosing the right structure and understanding tax benefits to getting the best rates and avoiding common mistakes. Written for Australian business owners by finance specialists.
            </p>

            <p className="text-sm text-gray-400 mt-4" data-testid="guide-route-priority-text">
              Start with the right service page, then continue in the secure portal.
            </p>

            <div className="mt-6 flex flex-wrap gap-3" data-testid="guide-hero-cta-row">
              <Link to="/business-finance" data-testid="guide-hero-primary-link">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-base rounded-xl" data-testid="guide-hero-primary-cta">
                  Check Your Options <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href={PORTAL_LINKS.COMMERCIAL} data-testid="guide-hero-secondary-link">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:text-white px-8 py-6 text-base rounded-xl" data-testid="guide-hero-secondary-cta">
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-navy-900 rounded-xl border border-white/5 p-8">
            <h2 className="text-lg font-heading text-white uppercase mb-4">In This Guide</h2>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                'What Is Equipment Finance?',
                'Types of Equipment Finance',
                'Chattel Mortgage vs Finance Lease',
                'Tax Benefits of Equipment Finance',
                'The Approval Process',
                'Interest Rates Explained',
                'Deposit Requirements',
                'Common Mistakes to Avoid',
                'When to Refinance Equipment',
                'Frequently Asked Questions',
              ].map((item, i) => (
                <span key={i} className="text-gray-300 text-sm flex items-center gap-2 py-1">
                  <span className="text-purple-400 font-mono text-xs">{String(i + 1).padStart(2, '0')}</span>
                  {item}
                </span>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* Section 1: What Is Equipment Finance? */}
      <section className="py-16 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">What Is Equipment Finance?</h2>
          <p className="text-gray-300 leading-relaxed mb-4">Equipment finance is a broad term covering any form of commercial lending used to acquire business equipment, machinery, vehicles, or other productive assets. Rather than paying the full purchase price upfront — which can strain cash reserves and limit business flexibility — equipment finance spreads the cost over regular payments that align with the revenue the equipment helps generate.</p>
          <p className="text-gray-300 leading-relaxed mb-4">The fundamental principle is straightforward: the equipment you are purchasing serves as security for the loan. This means you typically do not need to provide property, personal guarantees against your home, or other collateral. The lender's security is the equipment itself, which makes <Link to="/equipment-finance" className="text-purple-400 hover:text-purple-300 underline">equipment finance</Link> one of the most accessible forms of business funding available to Australian businesses.</p>
          <p className="text-gray-300 leading-relaxed mb-4">Equipment finance covers an enormous range of assets. <Link to="/truck-finance" className="text-purple-400 hover:text-purple-300 underline">Trucks and commercial vehicles</Link>, <Link to="/construction-equipment-finance" className="text-purple-400 hover:text-purple-300 underline">construction equipment</Link>, <Link to="/machinery-finance" className="text-purple-400 hover:text-purple-300 underline">manufacturing machinery</Link>, <Link to="/medical-equipment-finance" className="text-purple-400 hover:text-purple-300 underline">medical devices</Link>, <Link to="/it-equipment-finance" className="text-purple-400 hover:text-purple-300 underline">IT infrastructure</Link>, <Link to="/agriculture-equipment-finance" className="text-purple-400 hover:text-purple-300 underline">agricultural equipment</Link>, and <Link to="/business-equipment-finance" className="text-purple-400 hover:text-purple-300 underline">general business equipment</Link> — from cafe espresso machines to industrial CNC centres — all fall under the equipment finance umbrella.</p>
          <p className="text-gray-300 leading-relaxed">Financing equipment lets a business put an income-producing asset to work straight away and pay for it over its working life, instead of tying up cash reserves in a single purchase.</p>
        </div>
      </section>

      {/* Section 2: Types of Equipment Finance */}
      <section className="py-16 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Types of Equipment Finance in Australia</h2>
          <p className="text-gray-300 leading-relaxed mb-6">There are four primary equipment finance structures available to Australian businesses. Each has distinct features, tax implications, and suitability for different business situations.</p>

          <div className="space-y-6">
            {[
              {
                icon: DollarSign,
                title: 'Chattel Mortgage',
                path: '/chattel-mortgage',
                desc: 'A common structure for business assets. You own the asset from day one. The lender takes a "mortgage" over the chattel (asset) as security until the loan is repaid.',
                benefits: ['Immediate ownership of the asset', 'Claim GST on purchase price upfront', 'Interest and depreciation tax deductions', 'Optional balloon payment to reduce monthly costs'],
                bestFor: 'GST-registered businesses that want ownership, tax benefits, and flexibility. Ideal for trucks, equipment, and machinery.'
              },
              {
                icon: Clock,
                title: 'Finance Lease',
                path: '/finance-lease',
                desc: 'The lender purchases the asset and leases it to your business. You have full use during the term. At the end, you can purchase, return, or extend. Lease payments are generally deductible to the extent the asset is used for business.',
                benefits: ['Residual value can reduce regular payments', 'Payments generally deductible for business use', 'Flexible end-of-term options', 'No upfront GST claim needed'],
                bestFor: 'Businesses prioritising cashflow. Ideal for high-value equipment and fleet vehicles.'
              },
              {
                icon: Shield,
                title: 'Hire Purchase',
                path: '/hire-purchase',
                desc: 'You "hire" the asset while making fixed payments. Ownership transfers when the final payment is made. Simple, predictable, and straightforward.',
                benefits: ['Fixed payments — easy budgeting', 'Guaranteed ownership at end of term', 'Depreciation deductions available', 'Simple structure, easy to understand'],
                bestFor: 'Businesses wanting certainty and simplicity. Good for first-time equipment purchasers.'
              },
              {
                icon: RefreshCw,
                title: 'Operating Lease',
                path: '/operating-lease',
                desc: 'Use the equipment for an agreed period and return it. No residual value risk. Payments fully deductible. Popular for equipment you plan to upgrade regularly.',
                benefits: ['No residual value exposure', 'Payments fully tax deductible', 'Upgrade at each renewal', 'Maintenance can be included'],
                bestFor: 'IT equipment, fleet vehicles, and any asset you upgrade regularly.'
              },
            ].map((s, i) => (
              <div key={i} className="bg-navy-900 rounded-xl border border-white/5 p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center text-purple-400 flex-shrink-0">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <Link to={s.path} className="text-white font-heading font-bold text-xl uppercase hover:text-purple-400 transition-colors">{s.title}</Link>
                    <p className="text-gray-300 leading-relaxed mt-2">{s.desc}</p>
                  </div>
                </div>
                <div className="ml-14">
                  <ul className="space-y-2 mb-4">
                    {s.benefits.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-gray-300 text-sm"><Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />{b}</li>
                    ))}
                  </ul>
                  <p className="text-purple-400 text-sm"><strong>Best for:</strong> {s.bestFor}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Chattel Mortgage vs Finance Lease */}
      <section className="py-16 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Chattel Mortgage vs Finance Lease: A Detailed Comparison</h2>
          <p className="text-gray-300 leading-relaxed mb-6">The chattel mortgage vs finance lease decision is one of the most common questions in Australian equipment finance. Both are excellent structures, but they suit different business situations. Use our <Link to="/lease-vs-buy-calculator" className="text-purple-400 hover:text-purple-300 underline">lease vs buy calculator</Link> to compare the actual costs for your specific scenario.</p>

          <div className="bg-navy-800 rounded-xl border border-white/5 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/80 font-semibold p-4">Feature</th>
                  <th className="text-center text-white/80 font-semibold p-4"><Link to="/chattel-mortgage" className="text-purple-400 hover:text-purple-300">Chattel Mortgage</Link></th>
                  <th className="text-center text-white/80 font-semibold p-4"><Link to="/finance-lease" className="text-purple-400 hover:text-purple-300">Finance Lease</Link></th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Ownership', 'From day one', 'At end of term (optional)'],
                  ['GST claim', 'Upfront on purchase', 'With each payment'],
                  ['Monthly payment', 'Higher', 'Lower'],
                  ['Tax deductions', 'Interest + depreciation', 'Lease payments (business-use portion)'],
                  ['Balance sheet', 'Asset + liability', 'Right-of-use asset'],
                  ['End of term', 'You own it', 'Buy, return, or extend'],
                  ['Balloon/residual', 'Optional', 'Typical'],
                ].map(([feature, cm, fl], i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4 text-gray-400 font-semibold">{feature}</td>
                    <td className="p-4 text-gray-300 text-center">{cm}</td>
                    <td className="p-4 text-gray-300 text-center">{fl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-300 leading-relaxed mb-4"><strong className="text-white">Choose a chattel mortgage if:</strong> You are GST-registered, want immediate ownership, plan to keep the asset long-term, and want to claim the GST upfront. This is the default choice for most <Link to="/truck-finance" className="text-purple-400 hover:text-purple-300 underline">truck finance</Link> and <Link to="/equipment-finance" className="text-purple-400 hover:text-purple-300 underline">equipment finance</Link> applications.</p>
          <p className="text-gray-300 leading-relaxed"><strong className="text-white">Choose a finance lease if:</strong> You prioritise lower monthly payments, want flexibility at end of term, are not GST-registered, or plan to upgrade the equipment after a few years. Popular for fleet vehicles and technology equipment.</p>
        </div>
      </section>

      {/* Section 4: Tax Benefits */}
      <section className="py-16 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Tax Benefits of Equipment Finance</h2>
          <p className="text-gray-300 leading-relaxed mb-4">One of the most significant advantages of equipment finance is the tax benefits available to Australian businesses. The specific benefits depend on the finance structure you choose, but all structures offer meaningful tax advantages.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {[
              { title: 'GST Input Credits', desc: 'With a chattel mortgage, GST-registered businesses can claim the full GST on the purchase price as an input tax credit in their next BAS. On a $220,000 asset (inc. GST), that is approximately $20,000 back from the ATO.' },
              { title: 'Interest Deductions', desc: 'The interest component of your finance repayments is tax deductible under chattel mortgage and hire purchase structures. This effectively reduces the net cost of borrowing.' },
              { title: 'Depreciation', desc: 'Assets you own (chattel mortgage, hire purchase) can be depreciated over their effective life. Depreciation deductions reduce your taxable income without requiring any cash outlay.' },
              { title: 'Lease Deductions', desc: 'Finance lease and operating lease payments are generally deductible as a business expense to the extent the asset is used to earn business income. Your accountant can confirm the treatment for your situation.' },
            ].map((b, i) => (
              <div key={i} className="bg-navy-900 rounded-xl p-6 border border-white/5">
                <h3 className="text-white font-semibold mb-2">{b.title}</h3>
                <p className="text-gray-400 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-300 leading-relaxed">Always consult your accountant for advice specific to your business structure and circumstances. The tax treatment of equipment finance can vary based on the structure chosen, the type of asset, and your business entity type. Flash Funding works alongside your accountant to ensure you choose the structure that maximises your tax position.</p>
        </div>
      </section>

      {/* Section 5: Approval Process */}
      <section className="py-16 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">The Equipment Finance Approval Process</h2>
          <p className="text-gray-300 leading-relaxed mb-6">The equipment finance approval process has been streamlined significantly by digital technology and the growth of specialist non-bank lenders. Understanding what to expect helps you prepare and achieve the fastest possible outcome.</p>

          <div className="space-y-6 mb-6">
            {[
              { step: '01', title: 'Application', desc: 'Provide your business details, the equipment information (type, value, supplier), and basic financial information. With Flash Funding, this takes approximately 5-10 minutes through our online application or Application Form tool.' },
              { step: '02', title: 'Credit Assessment', desc: 'The lender reviews your application. For standard (full-doc) applications, they assess financial statements, bank statements, credit history, and the asset. For low-doc applications, the focus is on ABN history and bank statement activity.' },
              { step: '03', title: 'Approval & Offer', desc: 'If approved, the lender issues a formal offer detailing the rate, term, repayment amount, fees, and conditions. Flash Funding presents offers from multiple lenders so you can compare and choose the best option.' },
              { step: '04', title: 'Acceptance & Settlement', desc: 'You accept the offer, sign the finance documents, and the lender pays the equipment supplier directly. You take delivery of the equipment and start using it immediately.' },
            ].map((s) => (
              <div key={s.step} className="flex gap-6">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">{s.step}</div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                  <p className="text-gray-300">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-heading text-white uppercase mb-4">Documents Typically Required</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {['Valid driver\'s licence or photo ID', '3-6 months business bank statements', 'Latest tax return or financial statements', 'Equipment quote or invoice from supplier', 'ABN certificate or ASIC extract', 'Declaration of financial position (some lenders)'].map((doc, i) => (
              <div key={i} className="flex items-start gap-3 text-gray-300"><Check className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" /><span>{doc}</span></div>
            ))}
          </div>

          <p className="text-gray-300 leading-relaxed">For <Link to="/low-doc-asset-finance" className="text-purple-400 hover:text-purple-300 underline">low-doc applications</Link>, only a licence, ABN, and bank statements are typically required. This makes the process significantly faster for businesses that do not have formal financials readily available.</p>
        </div>
      </section>

      {/* Section 6: Interest Rates */}
      <section className="py-16 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Equipment Finance Interest Rates Explained</h2>
          <p className="text-gray-300 leading-relaxed mb-4">Equipment finance rates vary widely between lenders and depend on your business profile, credit history, the asset and the loan structure. Understanding what influences your rate helps you position your application for the best possible outcome.</p>

          <h3 className="text-xl font-heading text-white uppercase mb-4">Factors That Affect Your Rate</h3>
          <div className="space-y-4 mb-6">
            {[
              { title: 'Your Business Profile', desc: 'ABN age, revenue, profitability, and credit history. Longer-established businesses with clean credit get the best rates.' },
              { title: 'The Equipment', desc: 'New equipment attracts lower rates than used. Premium brands hold value better and receive preferential pricing.' },
              { title: 'Deposit Amount', desc: 'A deposit reduces the lender’s risk and can improve the rate or terms offered. No-deposit finance is available from some lenders for stronger applications.' },
              { title: 'Finance Amount', desc: 'Larger amounts ($200K+) sometimes attract better rates due to lender economies of scale.' },
              { title: 'The Lender', desc: 'Different lenders specialise in different segments. A specialist truck lender may offer better rates than a generalist bank for truck finance.' },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <DollarSign className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-semibold">{f.title}:</span>
                  <span className="text-gray-300 ml-1">{f.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-300 leading-relaxed">Comparing more than one lender is one of the most effective ways to find a better rate and structure, because each lender prices risk differently. Use our <Link to="/equipment-finance-calculator" className="text-purple-400 hover:text-purple-300 underline">equipment finance calculator</Link> to model different rate scenarios and see the impact on your monthly payments.</p>
        </div>
      </section>

      {/* Section 7: Deposit Requirements */}
      <section className="py-16 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Equipment Finance Deposit Requirements</h2>
          <p className="text-gray-300 leading-relaxed mb-4">One of the most common questions is "how much deposit do I need?" The answer varies significantly based on your business profile, the equipment, and the lender.</p>

          <div className="bg-navy-800 rounded-xl border border-white/5 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/80 font-semibold p-4">Scenario</th>
                  <th className="text-center text-white/80 font-semibold p-4">Deposit</th>
                  <th className="text-right text-white/80 font-semibold p-4">Impact on Rate</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Established business, new asset', '0-10%', 'Best available rates'],
                  ['Established business, used asset', '0-15%', 'Competitive rates'],
                  ['Newer business (1-2 year ABN)', '10-20%', 'Moderate improvement'],
                  ['Startup (<12 months)', '20-30%', 'Significantly improves approval'],
                  ['Impaired credit', '20-40%', 'May be required for approval'],
                ].map(([scenario, deposit, impact], i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4 text-gray-300">{scenario}</td>
                    <td className="p-4 text-purple-400 font-mono text-center">{deposit}</td>
                    <td className="p-4 text-gray-400 text-right text-xs">{impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-300 leading-relaxed">Use our <Link to="/equipment-finance-calculator" className="text-purple-400 hover:text-purple-300 underline">equipment finance calculator</Link> to see exactly how different deposit amounts affect your monthly repayments. Even a $10,000 deposit on a $150,000 asset can make a meaningful difference to your monthly cashflow.</p>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="py-12 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-heading text-white uppercase mb-2">Ready to Compare Finance Options?</h3>
              <p className="text-gray-300 text-sm">Choose your path first, then submit a guided application when ready.</p>
            </div>
            <Link to="/business-finance" className="flex-shrink-0" data-testid="guide-mid-primary-link">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-5 text-base rounded-xl whitespace-nowrap" data-testid="guide-mid-primary-cta">
                Check Your Options <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 8: Common Mistakes */}
      <section className="py-16 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Common Mistakes Businesses Make with Equipment Finance</h2>
          <p className="text-gray-300 leading-relaxed mb-6">After processing thousands of equipment finance applications, Flash Funding has identified the most common mistakes that cost Australian businesses money. Avoiding these errors can save you thousands over the life of your finance.</p>

          <div className="space-y-6">
            {[
              { title: 'Accepting the First Offer', desc: 'Lenders price the same scenario differently, and on a large loan even a small rate difference adds up to thousands of dollars over the term. Compare more than one option before you commit.' },
              { title: 'Choosing the Wrong Finance Structure', desc: 'A business that could save $10,000 in GST with a chattel mortgage instead takes a finance lease because they did not understand the difference. The wrong structure costs real money. Get advice before committing.' },
              { title: 'Ignoring Balloon Payments', desc: 'Setting a high balloon reduces monthly payments but creates a large obligation at the end. Many businesses are caught off guard when the balloon falls due. Plan for it from the start — refinance, pay out, or trade in.' },
              { title: 'Not Considering Total Cost', desc: 'A lower monthly payment does not always mean a cheaper loan. A 7-year term costs significantly more in total interest than a 5-year term, even though the monthly payment is lower. Look at the total cost, not just the monthly figure.' },
              { title: 'Failing to Shop Around for Rates', desc: 'Loyal bank customers assume their bank offers the best rate. In reality, specialist lenders frequently beat the major banks on equipment finance rates. Using a broker like Flash Funding creates competitive tension.' },
              { title: 'Not Getting Pre-Approved', desc: 'Without pre-approval, you cannot move quickly when the right equipment comes up for sale — especially at auction. Pre-approval gives you certainty and negotiating power.' },
            ].map((m, i) => (
              <div key={i} className="flex items-start gap-4">
                <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-1">{m.title}</h3>
                  <p className="text-gray-300 text-sm">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 9: When to Refinance */}
      <section className="py-16 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">When to Refinance Your Equipment Finance</h2>
          <p className="text-gray-300 leading-relaxed mb-4"><Link to="/asset-refinance" className="text-purple-400 hover:text-purple-300 underline">Asset refinancing</Link> is one of the most underutilised tools in equipment finance. Many businesses continue paying higher rates long after market conditions or their own credit profile have improved.</p>
          <p className="text-gray-300 leading-relaxed mb-4">Consider refinancing when:</p>
          <ul className="space-y-3 mb-6">
            {[
              'Interest rates have dropped since you originally took out the loan',
              'Your business credit profile has improved significantly',
              'You are on a higher rate because you were a startup when you first applied',
              'You want to lower monthly repayments to improve cashflow',
              'You want to consolidate multiple equipment loans into one',
              'You need to release equity in owned equipment for business growth',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-300"><Check className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" /><span>{item}</span></li>
            ))}
          </ul>
          <p className="text-gray-300 leading-relaxed">Use our <Link to="/asset-refinance-calculator" className="text-purple-400 hover:text-purple-300 underline">asset refinance calculator</Link> to see how much you could save by refinancing at a lower rate. Remember to factor in any break costs on your existing loan and establishment fees on the new one.</p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">Equipment Finance Calculators</h2>
          <p className="text-gray-300 leading-relaxed mb-6">Use our free online calculators to model different finance scenarios and make informed decisions:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { path: '/equipment-finance-calculator', title: 'Equipment Finance Calculator', desc: 'Estimate monthly repayments for any equipment purchase. Adjust price, deposit, rate, term, and balloon.' },
              { path: '/truck-finance-calculator', title: 'Truck Finance Calculator', desc: 'Calculate repayments specifically for commercial trucks — prime movers, rigids, tippers, and trailers.' },
              { path: '/lease-vs-buy-calculator', title: 'Lease vs Buy Calculator', desc: 'Compare the true cost of leasing versus buying equipment side by side.' },
              { path: '/asset-refinance-calculator', title: 'Asset Refinance Calculator', desc: 'See how much you could save by refinancing existing equipment at a lower rate.' },
            ].map((c, i) => (
              <Link key={i} to={c.path} className="bg-navy-800 rounded-xl p-6 border border-white/5 hover:border-purple-500/30 transition-colors group flex items-start gap-4">
                <Calculator className="h-6 w-6 text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-2">{c.title}</h3>
                  <p className="text-gray-400 text-sm">{c.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* State Lending Chapter */}
      <section className="py-16 bg-navy-900" data-testid="guide-state-lending-chapter">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-4">Equipment Finance Across Australia</h2>
          <p className="text-gray-300 leading-relaxed mb-8">
            Flash Funding works with businesses in every state and territory. Lenders assess your business, the asset and your ability to repay rather than your location, so the same lender panel is available wherever you operate.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'canberra', 'hobart', 'darwin'].map((city) => (
              <Link
                key={city}
                to={`/locations/${city}`}
                className="bg-navy-800 rounded-xl p-4 border border-white/5 hover:border-purple-500/30 transition-colors text-sm text-white"
                data-testid={`guide-location-link-${city}`}
              >
                {city.charAt(0).toUpperCase() + city.slice(1)}
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 text-sm">
            <Link to="/locations" className="text-purple-300 hover:text-purple-200" data-testid="guide-state-hub-link">All Locations</Link>
            <Link to="/equipment-finance" className="text-purple-300 hover:text-purple-200">Equipment Finance Product Page</Link>
            <Link to="/low-doc-asset-finance" className="text-purple-300 hover:text-purple-200">Low Doc Asset Finance</Link>
            <Link to="/equipment-finance-calculator" className="text-purple-300 hover:text-purple-200">Equipment Calculator</Link>
            <Link to="/learn" className="text-purple-300 hover:text-purple-200">Learn Guides</Link>
          </div>
        </div>
      </section>

      {/* Section 10: Submission Quality Checklist */}
      <section className="py-16 bg-navy-800" data-testid="guide-submission-quality-section">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-4">Submission Quality Checklist (Before You Apply)</h2>
          <p className="text-gray-300 leading-relaxed mb-6">
            Strong approval outcomes are usually driven by file quality rather than headline marketing rates. Use this checklist to reduce conditional churn,
            improve settlement speed, and preserve negotiating leverage with lenders.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {[
              'Define the exact asset program and supplier pathway before application.',
              'Present liabilities and existing commitments clearly (avoid partial disclosure).',
              'Align term and balloon to real utilisation and replacement horizon.',
              'Use state playbooks to adapt narrative to local operating conditions.',
              'Model repayments first using calculator scenarios (base / stress / conservative).',
              'Sequence documents in lender-ready order to avoid avoidable condition loops.',
            ].map((item, idx) => (
              <div key={idx} className="bg-navy-900 border border-white/10 rounded-xl p-4 flex items-start gap-3" data-testid={`guide-submission-check-${idx + 1}`}>
                <Check className="h-4 w-4 text-purple-400 mt-1" />
                <span className="text-gray-200 text-sm">{item}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 text-sm">
            <Link to="/locations" className="text-purple-300 hover:text-purple-200" data-testid="guide-checklist-state-hub-link">Locations</Link>
            <a href={PORTAL_LINKS.COMMERCIAL} className="text-purple-300 hover:text-purple-200" data-testid="guide-checklist-apply-link">See What You Can Get Approved For</a>
            <Link to="/equipment-finance-calculator" className="text-purple-300 hover:text-purple-200" data-testid="guide-checklist-equipment-calc-link">Equipment Calculator</Link>
            <Link to="/asset-refinance-calculator" className="text-purple-300 hover:text-purple-200" data-testid="guide-checklist-refinance-calc-link">Refinance Calculator</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-navy-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} className="bg-navy-900 rounded-xl border border-white/5 px-6">
                <AccordionTrigger className="text-white text-left py-5 hover:text-purple-400">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-gray-300 pb-5 leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Clustered Related Finance Guides */}
      <section className="py-16 bg-navy-900" data-testid="related-finance-guides">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-10">Related Finance Guides</h2>
          {clusters.assetFinance.length > 0 && (
            <div className="mb-10">
              <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Asset Finance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clusters.assetFinance.map((page, idx) => (
                  <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-colors group">
                    <h4 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-2">{page.title}</h4>
                    <p className="text-gray-400 text-sm mb-3">{page.desc}</p>
                    <span className="text-purple-400 text-sm flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {clusters.financeStructures.length > 0 && (
            <div className="mb-10">
              <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Finance Structures</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clusters.financeStructures.map((page, idx) => (
                  <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-colors group">
                    <h4 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-2">{page.title}</h4>
                    <p className="text-gray-400 text-sm mb-3">{page.desc}</p>
                    <span className="text-purple-400 text-sm flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {clusters.financeTools.length > 0 && (
            <div className="mb-10">
              <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Finance Tools</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clusters.financeTools.map((page, idx) => (
                  <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-colors group flex items-start gap-3">
                    <Calculator className="h-5 w-5 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-1">{page.title}</h4>
                      <p className="text-gray-400 text-sm">{page.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {clusters.stateGuides?.length > 0 && (
            <div className="mb-10" data-testid="guide-related-state-guides">
              <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">State &amp; Regional Guides</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {clusters.stateGuides.map((page, idx) => (
                  <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-4 border border-white/5 hover:border-purple-500/30 transition-colors group" data-testid={`guide-related-state-link-${page.path.replace(/[^a-z0-9]+/gi, '-')}`}>
                    <h4 className="text-white text-sm font-semibold group-hover:text-purple-400 transition-colors mb-1">{page.title}</h4>
                    <p className="text-gray-400 text-xs">{page.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-b from-navy-900 to-purple-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading text-white uppercase mb-4">Ready to Finance Your Equipment?</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">Move to the business finance path first, then submit your guided application when ready.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/business-finance" data-testid="guide-bottom-primary-link">
              <Button data-testid="cta-bottom" className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-lg rounded-xl">Check Your Options <ArrowRight className="ml-2 h-5 w-5" /></Button>
            </Link>
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="guide-bottom-secondary-link">
              <Button variant="outline" className="border-gray-600 text-gray-300 hover:text-white px-10 py-6 text-lg rounded-xl">See What You Can Get Approved For</Button>
            </a>
          </div>
          <p className="text-gray-500 text-sm mt-6">No obligation. No credit check. Get your options quickly.</p>
        </div>
      </section>
    </>
  );
}
